import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Avatar, Tooltip, Tabs, Tab } from '@material-ui/core';
import type { AppState } from 'src/reducers';
import styles, { usePlanQueueStyles } from './PlanQueue.styles';
import { AddToAssortmentItem, PlanItem } from 'src/pages/AssortmentBuild/Planning.types';
import { getIconBadgeColor, processCartItems, processPlanItems } from './PlanQueue.utils';
import {
  getActiveContextPlanType,
  hasContextsWithPlanTypeAllocation,
} from 'src/pages/PerspectiveSelection/PerspectiveSelection.selectors';
import { PlanType } from 'configuration-schemas/general';
import { AssortmentPlanTab } from './AssortmentPlanTab';
import { CartPlanTab } from './CartPlanTab';
import { AllocationPlanTab } from './AllocationPlanTab';
import { PlanErrors, ProcessedCartData, ProcessedPlanData } from './PlanQueue.types';
import ServiceContainer from 'src/ServiceContainer';
import { concat } from 'lodash';

interface PlanQueueTabLabelProps {
  itemCount: number;
  text: string;
  tooltipText: string;
  badgeColor: string;
}

const PlanQueueTabLabel = ({ itemCount, tooltipText, text, badgeColor }: PlanQueueTabLabelProps) => {
  const planQueueStyles = usePlanQueueStyles(badgeColor)();
  return (
    <div className={styles.titleText}>
      <Tooltip title={tooltipText} placement="top-start" arrow>
        <Avatar className={planQueueStyles.small} variant="square">
          <span>{itemCount}</span>
        </Avatar>
      </Tooltip>
      <span className={planQueueStyles.tabLabel}>{text}</span>
    </div>
  );
};

interface PlanQueueProps {
  allocationItems: PlanItem[];
  assortmentItems: PlanItem[];
  cartItems: AddToAssortmentItem[];
  planErrors: PlanErrors;
  planType: PlanType;
  showAllocation: boolean;
}

const PlanQueue = ({
  allocationItems,
  assortmentItems,
  cartItems,
  planErrors,
  planType,
  showAllocation,
}: PlanQueueProps) => {
  const [selectedTabIndex, setSelectedTabIndex] = useState(() => {
    // default to correct tab on initial load based on active context's planType
    // NOTE: we don't need to capture changes via useEffect on `planType` changes
    // because the PlanQueue is destroyed before switching to a new tab|view
    return planType === 'assortment' ? 0 : 1;
  });
  const [search, setSearch] = useState('');

  const handleTabChange = (_event: unknown, selectedIndex: number) => {
    setSelectedTabIndex(selectedIndex);
  };

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
  };

  useEffect(() => {
    if (assortmentItems.length === 0 || allocationItems.length === 0) return;

    const missingIds = concat(assortmentItems, allocationItems)
      .filter((pi) => !pi.description)
      .map((pi) => pi.id);
    if (missingIds.length > 0) {
      ServiceContainer.loggingService.error(`PlanQueue: ${missingIds.join(', ')} item(s) missing description`);
    }
  }, [assortmentItems, allocationItems]);

  const filteredAsstItems = assortmentItems.filter((pi) =>
    (pi.description ?? pi.id).toLowerCase().includes(search.toLowerCase()),
  );
  const processedAsstData: ProcessedPlanData = processPlanItems(filteredAsstItems);
  const processedCartData: ProcessedCartData = processCartItems(cartItems);
  const addItemsCount = processedCartData.processing.items.length + processedCartData.failed.items.length;

  const asstBadgeColor = getIconBadgeColor({
    planPending: processedAsstData.pending.items,
    planProcessing: processedAsstData.processing.items,
    planCompleted: processedAsstData.completed.items,
    planFailed: processedAsstData.failed.items,
    cssColorFormat: true,
  });
  const cartBadgeColor = getIconBadgeColor({
    addProcessing: processedCartData.processing.items,
    addFailed: processedCartData.failed.items,
    cssColorFormat: true,
  });

  let filteredAllocItems = [];
  let processedAllocData: ProcessedPlanData = {} as ProcessedPlanData;
  let allocBadgeColor = '';

  if (showAllocation) {
    filteredAllocItems = allocationItems.filter((pi) =>
      (pi.description ?? pi.id).toLowerCase().includes(search.toLowerCase()),
    );
    processedAllocData = processPlanItems(filteredAllocItems);
    allocBadgeColor = getIconBadgeColor({
      planProcessing: processedAllocData.processing.items,
      planCompleted: processedAllocData.completed.items,
      planFailed: processedAllocData.failed.items,
      cssColorFormat: true,
    });
  }

  const tabClasses = { root: styles.tabRoot, selected: styles.active, wrapper: styles.tabWrapper };

  return (
    <div className={styles.container}>
      <Tabs
        classes={{ root: styles.tabsRoot, flexContainer: styles.tabsFlexContainer }}
        value={selectedTabIndex}
        onChange={handleTabChange}
        indicatorColor="secondary"
        TabIndicatorProps={{ className: styles.tabIndicator }}
      >
        <Tab
          classes={tabClasses}
          label={
            <PlanQueueTabLabel
              text={'Assortment'}
              tooltipText={`Plan status for ${filteredAsstItems.length} item(s) available.`}
              itemCount={filteredAsstItems.length}
              badgeColor={asstBadgeColor}
            />
          }
        />
        {showAllocation ? (
          <Tab
            classes={tabClasses}
            label={
              <PlanQueueTabLabel
                text={'Allocation'}
                tooltipText={`Plan status for ${filteredAllocItems.length} item(s) available.`}
                itemCount={filteredAllocItems.length}
                badgeColor={allocBadgeColor}
              />
            }
          />
        ) : null}
        <Tab
          classes={tabClasses}
          label={
            <PlanQueueTabLabel
              text={'Cart'}
              tooltipText={`Processing: ${processedCartData.processing.items.length}, Failed: ${processedCartData.failed.items.length}`}
              itemCount={addItemsCount}
              badgeColor={cartBadgeColor}
            />
          }
        />
      </Tabs>

      {/* Assortment Planning Tab content */}
      {selectedTabIndex === 0 ? (
        <AssortmentPlanTab
          hasError={planErrors.assortment}
          data={processedAsstData}
          count={filteredAsstItems.length}
          search={search}
          onSearchChange={handleSearch}
        />
      ) : null}

      {/* Allocation Planning Tab content */}
      {showAllocation && selectedTabIndex === 1 ? (
        <AllocationPlanTab
          hasError={planErrors.allocation}
          data={processedAllocData}
          count={filteredAllocItems.length}
          search={search}
          onSearchChange={handleSearch}
        />
      ) : null}

      {/* Cart Tab content */}
      {(showAllocation && selectedTabIndex === 2) || (!showAllocation && selectedTabIndex === 1) ? (
        <CartPlanTab
          hasError={planErrors.cart}
          data={processedCartData}
          count={cartItems.length}
          search={search}
          onSearchChange={handleSearch}
        />
      ) : null}
    </div>
  );
};

function mapStateToProps(state: AppState): PlanQueueProps {
  const { addToAsstItems, assortmentPlanError, addToAsstError, allocationPlanError, allocPlanItems, planItems } =
    state.planTracker;
  const planType = getActiveContextPlanType(state);
  const showAllocation = hasContextsWithPlanTypeAllocation(state);
  return {
    assortmentItems: planItems,
    allocationItems: allocPlanItems,
    cartItems: addToAsstItems,
    planErrors: {
      assortment: assortmentPlanError,
      allocation: allocationPlanError,
      cart: addToAsstError,
    },
    planType,
    showAllocation,
  };
}

export default connect(mapStateToProps)(PlanQueue);
